import * as _ from 'lodash';
import * as Promise from 'bluebird';
import { I18n } from 'react-redux-i18n-lite';
import * as classroomApi from '../services/api/classroomApi';
import * as backofficeApi from '../services/api/backofficeApi';
import * as messageAlert from './messageAlertAction';
import { isUserInstructor, isUserStudent } from '../services/jwt/jwtService';
import { REDUCER } from '../reducers/reducersConst';
import { ROLE, TASK_TABS_CONSTANTS } from '../const';
import * as taskService from '../services/domain/taskService';
import { getLogger } from '../services/logger';
import { setActivities } from './bookActions';

const debug = getLogger('actions:taskActions');

/**
 * Saves in store the tasks associated with the selected classroom
 * @param tasks
 * @returns {{type: string, tasks: array}}
 */
export const setTasks = tasks => {
  return {
    type: REDUCER.TASK.SET_TASKS,
    tasks
  };
};

/**
 * Saves in store the current tasks associated with the selected classroom
 * @param datesRangeFilter
 * @returns {{type: string, datesRangeFilter: array}}
 */
export const setTasksDatesRangeFilter = datesRangeFilter => {
  return {
    type: REDUCER.TASK.SET_DATE_RANGE_TASKS_FILTER,
    datesRangeFilter
  };
};

/**
 * Saves in store the current tasks associated with the selected classroom
 * @param keywordsFilter
 * @returns {{type: string, keywordsFilter: array}}
 */
export const setTasksKeywordsFilter = keywordsFilter => {
  return {
    type: REDUCER.TASK.SET_KEYWORDS_TASKS_FILTER,
    keywordsFilter
  };
};

/**
 * Saves in store the current tasks associated with the selected classroom
 * @param tasks
 * @returns {{type: string, tasks: array}}
 */
export const setCurrentTasks = (tasks = []) => {
  return {
    type: REDUCER.TASK.SET_CURRENT_TASKS,
    tasks
  };
};

/**
 * Saves in store the current tasks page
 * @param currentPage
 * @returns {{type: string, currentPage: number}}
 */
export const setCurrentTasksCurrentPage = (currentPage = 0) => {
  return {
    type: REDUCER.TASK.SET_CURRENT_TASKS_CURRENT_PAGE,
    currentPage
  };
};

/**
 * Saves in store the number of pages
 * @param pages
 * @returns {{type: string, pages: number}}
 */
export const setCurrentTasksPages = (pages = 1) => {
  return {
    type: REDUCER.TASK.SET_CURRENT_TASKS_PAGES,
    pages
  };
};

/**
 * Saves in store the number of pages
 * @param fetching
 * @returns {{type: string, fetching: boolean}}
 */
export const setCurrentTasksFetching = (fetching = false) => {
  return {
    type: REDUCER.TASK.SET_CURRENT_TASKS_FETCHING,
    fetching
  };
};

/**
 * Saves in store the finished tasks associated with the selected classroom
 * @param tasks
 * @returns {{type: string, tasks: array}}
 */
export const setFinishedTasks = (tasks = []) => {
  return {
    type: REDUCER.TASK.SET_FINISHED_TASKS,
    tasks
  };
};

/**
 * Saves in store the finished tasks page
 * @param currentPage
 * @returns {{type: string, currentPage: number}}
 */
export const setFinishedTasksCurrentPage = (currentPage = 0) => {
  return {
    type: REDUCER.TASK.SET_FINISHED_TASKS_CURRENT_PAGE,
    currentPage
  };
};

/**
 * Saves in store the number of pages
 * @param pages
 * @returns {{type: string, pages: number}}
 */
export const setFinishedTasksPages = (pages = 1) => {
  return {
    type: REDUCER.TASK.SET_FINISHED_TASKS_PAGES,
    pages
  };
};

/**
 * Saves in store the number of pages
 * @param fetching
 * @returns {{type: string, fetching: boolean}}
 */
export const setFinishedTasksFetching = (fetching = false) => {
  return {
    type: REDUCER.TASK.SET_FINISHED_TASKS_FETCHING,
    fetching
  };
};

/**
 * Saves in store the finished tasks associated with the selected classroom
 * @param tasks
 * @returns {{type: string, tasks: array}}
 */
export const setFutureTasks = (tasks = []) => {
  return {
    type: REDUCER.TASK.SET_FUTURE_TASKS,
    tasks
  };
};

/**
 * Saves in store the finished tasks page
 * @param currentPage
 * @returns {{type: string, currentPage: number}}
 */
export const setFutureTasksCurrentPage = (currentPage = 0) => {
  return {
    type: REDUCER.TASK.SET_FUTURE_TASKS_CURRENT_PAGE,
    currentPage
  };
};

/**
 * Saves in store the number of pages
 * @param pages
 * @returns {{type: string, pages: number}}
 */
export const setFutureTasksPages = (pages = 1) => {
  return {
    type: REDUCER.TASK.SET_FUTURE_TASKS_PAGES,
    pages
  };
};

/**
 * Saves in store the number of pages
 * @param fetching
 * @returns {{type: string, fetching: boolean}}
 */
export const setFutureTasksFetching = (fetching = false) => {
  return {
    type: REDUCER.TASK.SET_FUTURE_TASKS_FETCHING,
    fetching
  };
};

/**
 * Saves in store the finished tasks associated with the selected classroom
 * @param tasks
 * @returns {{type: string, tasks: array}}
 */
export const setFilteredTasks = (tasks = []) => {
  return {
    type: REDUCER.TASK.SET_FILTERED_TASKS,
    tasks
  };
};

/**
 * Saves in store the finished tasks page
 * @param currentPage
 * @returns {{type: string, currentPage: number}}
 */
export const setFilteredTasksCurrentPage = (currentPage = 0) => {
  return {
    type: REDUCER.TASK.SET_FILTERED_TASKS_CURRENT_PAGE,
    currentPage
  };
};

/**
 * Saves in store the number of pages
 * @param pages
 * @returns {{type: string, pages: number}}
 */
export const setFilteredTasksPages = (pages = 1) => {
  return {
    type: REDUCER.TASK.SET_FILTERED_TASKS_PAGES,
    pages
  };
};

/**
 * Saves in store the number of pages
 * @param fetching
 * @returns {{type: string, fetching: boolean}}
 */
export const setFilteredTasksFetching = (fetching = false) => {
  return {
    type: REDUCER.TASK.SET_FILTERED_TASKS_FETCHING,
    fetching
  };
};

export const setTaskStudentsAssigned = studentsAssigned => {
  return {
    type: REDUCER.TASK.SET_STUDENTS_ASSIGNED,
    studentsAssigned
  };
};

export const setSelectedTask = (selectedTask = {}) => {
  return {
    type: REDUCER.TASK.SET_SELECTED_TASK,
    selectedTask
  };
};

export const setModalOpenTask = modalOpenTask => {
  return {
    type: REDUCER.TASK.SET_MODAL_OPEN_TASK,
    modalOpenTask
  };
};

export const setTaskComments = comments => {
  return {
    type: REDUCER.TASK.SET_EVALUATED_ASSIGNMENT_COMMENTS,
    comments
  };
};

export const setTaskResponseComment = (comment, task, user) => {
  return {
    type: REDUCER.TASK.SET_TASK_RESPONSE_COMMENTS,
    comment,
    task,
    user
  };
};

export const setActiveTab = activeTaskTab => {
  return {
    type: REDUCER.TASK.SET_ACTIVE_TAB,
    activeTaskTab
  };
};

export const setTaskContentsNotFound = (contentsNotFound = false) => {
  return {
    type: REDUCER.TASK.SET_CONTENT_NOT_FOUND,
    contentsNotFound
  };
};

export const setActivitiesState = () => {
  return {
    type: REDUCER.TASK.SET_ACTIVATE_STATE
  };
};

/**
 * Gets all tasks of a classroom and its results
 * @param classroomId
 * @param taskId
 * @param studentId
 * @returns {function(*, *)}
 */
export const getClassroomTasksWithResults = (classroomId, taskId = undefined, studentId = undefined) => {
  return (dispatch, getState) => {
    if (classroomId) {
      const state = getState();
      const currentUser = state.account.user._id;
      dispatch(getCurrentAssignments(classroomId, currentUser));
      dispatch(getFinishedAssignments(classroomId, currentUser));
      dispatch(getFutureAssignments(classroomId, currentUser));
      const selectedTask = state.task && state.task.selectedTask && state.task.selectedTask.id;
      const selectedStudent =
        state.assignmentsDrawerActions &&
        state.assignmentsDrawerActions.evaluatedStudent &&
        state.assignmentsDrawerActions.evaluatedStudent._id;
      if (
        taskId &&
        studentId &&
        taskId === selectedTask &&
        (currentUser === studentId || studentId === selectedStudent)
      ) {
        dispatch(updateSelectedTask(classroomId, taskId, { _id: studentId }));
      }
    }
  };
};

/**
 * Return the userIds of a Section. Section have a list os Users
 * */
export const getUserIds = sections => {
  if (!sections || _.isEmpty(sections)) return [];

  return _.keysIn(sections);
};

export const getSelectedTaskWithResults = (taskId, student, task) => {
  return async (dispatch, getState) => {
    let state = getState();
    let selectedTask = task ? task : state.task.selectedTask;
    const classroom = state.classrooms.activeClassrooms.find(c => c.id === selectedTask.parentSection);
    let theme = state.theme;
    if (taskId && selectedTask) {
      const sectionId = classroom.sectionId;
      const classroomId = classroom.id;
      const userId = student ? student._id : state.account.user._id;
      const selectedTaskWithResults = _.clone(selectedTask);
      selectedTaskWithResults.activityStructure = await taskService.updateTaskActivitiesStructure(
        taskId,
        sectionId,
        classroomId,
        userId,
        selectedTask
      );

      selectedTaskWithResults.withResults = true;
      selectedTaskWithResults.comments = await taskService.getTaskComments(userId, selectedTask, theme);

      if (_.isNil(selectedTaskWithResults.comments) || _.isNil(selectedTaskWithResults.comments.avatar)) {
        let teachers = await classroomApi.getClassroomTeachers(classroomId);
        if (teachers && teachers.length) {
          selectedTaskWithResults.teacherImage = teachers[0].picture;
        }
      }

      return dispatch(setSelectedTask(selectedTaskWithResults));
    }
  };
};

export const submitStudents = (task, selectedUsers) => {
  return (dispatch, getState) => {
    try {
      const userIds = selectedUsers.map(user => user._id);
      const classroom = getState().classrooms.activeClassrooms.find(c => c.id === task.parentSection);
      const classroomId = classroom.id;
      const taskId = task._id;

      taskService.updateStudentsFromTask(classroomId, taskId, userIds);
      dispatch(messageAlert.success(I18n.t('addUserToTaskReveal.taskAssignedAlert')));
    } catch (err) {
      dispatch(messageAlert.error(I18n.t('participants.messageAlert.participantsError')));
    }
  };
};

export const setEvaluatedAssignmentComments = evaluatedStudentId => {
  return async (dispatch, getState) => {
    const state = getState();
    const comments = await taskService.getTaskComments(evaluatedStudentId, state.task.selectedTask, state.theme);
    return dispatch(setTaskComments(comments));
  };
};

export const getAllTasks = classroomId => {
  return dispatch => {
    if (classroomId) {
      dispatch(getClassroomTasksWithResults(classroomId));
    }
  };
};

export const updateSelectedTask = (classroomId, taskId, user) => {
  return (dispatch, getState) => {
    classroomApi
      .getAssignmentById(classroomId, taskId)
      .then(res => {
        dispatch(getSelectedTaskWithResults(taskId, user, res.data));
      })
      .catch(error => {
        debug.error('[updateSelectedTask] error getting assignment ', error);
        dispatch(messageAlert.error(I18n.t('tasks.tasksErrors')));
      });
  };
};

export const getCurrentAssignments = (classroomId, userId, page) => {
  return (dispatch, getState) => {
    const filterParams = {
      status: TASK_TABS_CONSTANTS.IN_PROGRESS
    };

    const currentPage = page ? page : getState().task.currentTasks.currentPage;
    dispatch(setCurrentTasksFetching(true));
    dispatch(getAssignments(classroomId, userId, currentPage + 1, filterParams)).catch(() => setCurrentTasksFetching());
  };
};

export const getFinishedAssignments = (classroomId, userId, page) => {
  return (dispatch, getState) => {
    const filterParams = {
      status: TASK_TABS_CONSTANTS.FINISHED
    };
    if (classroomId) {
      const currentPage = page ? page : getState().task.finishedTasks.currentPage;
      dispatch(setFinishedTasksFetching(true));
      return dispatch(getAssignments(classroomId, userId, currentPage + 1, filterParams)).catch(() =>
        setFinishedTasksFetching()
      );
    }
  };
};

export const getFutureAssignments = (classroomId, userId, page) => {
  return (dispatch, getState) => {
    const filterParams = {
      status: TASK_TABS_CONSTANTS.FUTURE
    };
    if (classroomId) {
      const currentPage = page ? page : getState().task.futureTasks.currentPage;
      dispatch(setFutureTasksFetching(true));
      return dispatch(getAssignments(classroomId, userId, currentPage + 1, filterParams)).catch(() =>
        setFutureTasksFetching()
      );
    }
  };
};

export const getFilteredAssignments = (classroomId, userId, page, status) => {
  return (dispatch, getState) => {
    const taksFilter = getState().task;
    const filterParams = {
      startDate: taksFilter.datesRangeFilter ? taksFilter.datesRangeFilter.startDate : undefined,
      finishDate: taksFilter.datesRangeFilter ? taksFilter.datesRangeFilter.endDate : undefined,
      keywords: taksFilter.keywordsFilter,
      status: status
    };
    const currentPage = page ? page : getState().task.filteredTasks.currentPage;
    dispatch(setFilteredTasksFetching(true));
    return dispatch(getAssignments(classroomId, userId, currentPage + 1, filterParams)).catch(() =>
      setFilteredTasksFetching()
    );
  };
};

export const getAssignments = (classroomId, userId, page, filterParams) => {
  return (dispatch, getState) => {
    return classroomApi
      .getPaginatedAssignments(classroomId, userId, page, filterParams)
      .then(res => {
        const paginatedData = res.data;
        const totalPages = paginatedData.pagination.totalPages;
        if (isUserInstructor(getState())) {
          dispatch(getTasksForInstructor(paginatedData.tasks, totalPages, filterParams.status, classroomId));
        } else if (isUserStudent(getState())) {
          dispatch(getTasksForStudent(paginatedData.tasks, userId, totalPages, filterParams.status));
        }
      })
      .catch(error => {
        debug.error('[getAssignments] error getting assignments %s', error);
        dispatch(messageAlert.error(I18n.t('tasks.tasksErrors')));
      });
  };
};

/**
 * Gets the attempts for the given tasks in a section context
 * @param tasks
 * @param totalPages
 * @param assignmentStatus
 * @returns {function(*)}
 */
export const getTasksForInstructor = (tasks, totalPages, assignmentStatus = {}, classroomId) => {
  return dispatch => {
    // getActivitiesStateForInstructor(classroomId, tasks);
    return Promise.map(tasks, task => {
      let students = getUserIds(task.studentAttempts);
      let noLessonActivities = taskService.getActivitiesWithoutLessons(task.activities);
      return backofficeApi
        .getActivityTrackingSummary(task.parentSection, noLessonActivities, task.dueDate, students)
        .then(res => {
          task.resultSummary = taskService.getTaskResultsForInstructor(res.data);
          task.errorGettingResults = false;
        })
        .catch(err => {
          debug.error('[getTasksForInstructor] error getting results %s', err);
          task.errorGettingResults = true;
        });
    })
      .then(() => {
        let activitiesStatePromises = [];

        if (tasks.length) {
          tasks.forEach(task => {
            task.activitiesState = [];
            task.activitiesStateNotracking = [];

            if (Array.isArray(task.bookActivities)) {
              task.bookActivities.forEach(bookAct => {
                if (bookAct.activityType === 'https://w3id.org/xapi/smart/activity/unit') {
                  activitiesStatePromises.push(
                    backofficeApi.getActivitisStateInstructor(task.parentSection, bookAct.activityId).then(res => {
                      res.data.map(act => {
                        if (task.activities.find(activit => activit.activityId === act.activityId)) {
                          task.activitiesState.push(act);
                        }
                      });
                    })
                  );
                }
              });
            }
          });
        }

        if (!activitiesStatePromises.length) {
          dispatch(setActivitiesState());
          return Promise.resolve();
        }

        return Promise.all(activitiesStatePromises)
          .catch(err => {
            debug.error('[getTasksForInstructor] error getting activities state %s', err);
          })
          .finally(() => {
            dispatch(setActivitiesState());
          });
      })
      .then(() => {
        if (assignmentStatus.key === TASK_TABS_CONSTANTS.IN_PROGRESS.key) {
          dispatch(setCurrentTasksPages(totalPages));
          dispatch(setCurrentTasks(tasks));
          dispatch(setCurrentTasksFetching());
        } else if (assignmentStatus.key === TASK_TABS_CONSTANTS.FINISHED.key) {
          dispatch(setFinishedTasksPages(totalPages));
          dispatch(setFinishedTasks(tasks));
          dispatch(setFinishedTasksFetching());
        } else if (assignmentStatus.key === TASK_TABS_CONSTANTS.FUTURE.key) {
          dispatch(setFutureTasksPages(totalPages));
          dispatch(setFutureTasks(tasks));
          dispatch(setFutureTasksFetching());
        } else {
          dispatch(setFilteredTasksPages(totalPages));
          dispatch(setFilteredTasks(tasks));
          return dispatch(setFilteredTasksFetching());
        }
      });
  };
};

/**
 * Retrieves the results for the activities within the tasks for the student provided
 * @param tasks
 * @param studentId
 * @param assignmentStatus
 * @param totalPages
 * @returns {function(*)}
 */
export const getTasksForStudent = (tasks, studentId, totalPages, assignmentStatus = {}) => {
  return dispatch => {
    return Promise.map(tasks, task => {
      let students = [];
      students.push(studentId);
      let noLessonActivities = taskService.getActivitiesWithoutLessons(task.activities);
      return backofficeApi
        .getActivityTrackingSummary(task.parentSection, noLessonActivities, task.dueDate, students)
        .then(res => {
          task.resultSummary = taskService.getTaskResultsForStudent(res.data, studentId);
          task.errorGettingResults = false;
        })
        .catch(err => {
          debug.error('[getTasksAttemptsForStudent] error getting results %s', err);
          task.errorGettingResults = true;
        });
    }).then(() => {
      if (assignmentStatus.key === TASK_TABS_CONSTANTS.IN_PROGRESS.key) {
        dispatch(setCurrentTasksPages(totalPages));
        dispatch(setCurrentTasks(tasks));
        dispatch(setCurrentTasksFetching());
      } else if (assignmentStatus.key === TASK_TABS_CONSTANTS.FINISHED.key) {
        dispatch(setFinishedTasksPages(totalPages));
        dispatch(setFinishedTasks(tasks));
        dispatch(setFinishedTasksFetching());
      } else {
        dispatch(setFilteredTasksPages(totalPages));
        dispatch(setFilteredTasks(tasks));
        return dispatch(setFilteredTasksFetching());
      }
    });
  };
};
